import React, { useState, useEffect } from "react";
import AuthLayout from "../../components/AuthLayout";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast, { Toaster } from "react-hot-toast";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useApi } from "../lib/useApi";
import { Check } from "lucide-react";

const signupSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  workEmail: z.string().email("Please enter a valid email address"),
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
});

export default function Signup() {
  const { callApi } = useApi();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mess, setMess] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [userType, setUserType] = useState(null);

  (function () {
    function clearStorageOnBack(event) {
      // Triggered when page is restored via back/forward cache
      if (event.persisted) {
        localStorage.clear();
      }
    }

    window.addEventListener("pageshow", clearStorageOnBack);

    // Optional: also catch history back navigation
    window.addEventListener("popstate", function () {
      localStorage.clear();
    });
  })();

  useEffect(() => {
    const savedType = localStorage.getItem("userType");
    if (savedType) {
      setUserType(savedType);
      setSelectedType(savedType);
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  }, []);

  const form = useForm({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = async (data) => {
    const formPayload = new FormData();

    formPayload.append(
      "name",
      `${data?.firstName || ""} ${data?.lastName || ""}`
    );
    formPayload.append("email", data?.workEmail || "");
    formPayload.append("supportMail", data?.workEmail || "");
    formPayload.append("company", data?.companyName || "");

    const savedType = localStorage.getItem("userType") || "merchant";
    formPayload.append("isAgency", savedType === "agency" ? "true" : "false");

    try {
      const response = await callApi("/auth/merchantSignup", formPayload);
      const message = response?.message;

      setMess(message || "");
      toast.success(message || "Account created successfully!");
      localStorage.removeItem("userType");
      setIsSubmitted(true);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Something went wrong";

      toast.error(errorMessage);
    }
  };

  const handleUserType = (type) => {
    setSelectedType(type);
    setUserType(type);
    localStorage.setItem("userType", type);
    setShowModal(false);
  };

  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "";
  }, [showModal]);

  if (isSubmitted) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
        {/* Animated Background Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-0 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-lg w-full">
          {/* Success Card */}
          <div className="bg-background/95 backdrop-blur-sm border border-border rounded-2xl shadow-2xl p-8 md:p-10 text-center">
            {/* Success Icon with Animation */}
            <div className="mb-8 flex justify-center">
              <div className="relative">
                {/* Outer Ring */}
                <div className="absolute inset-0 w-24 h-24 bg-accent/20 rounded-full animate-ping" />
                {/* Middle Ring */}
                <div className="absolute inset-0 w-24 h-24 bg-accent/30 rounded-full animate-pulse" />
                {/* Icon Container */}
                <div className="relative w-24 h-24 bg-gradient-to-br from-accent to-accent/80 rounded-full flex items-center justify-center shadow-xl transform transition-all duration-500 hover:scale-110">
                  <Check className="w-12 h-12 text-accent-foreground stroke-[3] animate-in zoom-in duration-700" />
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="mb-8">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 text-balance animate-in fade-in slide-in-from-bottom-4 duration-500">
                Account Created!
              </h2>
              <p className="text-lg text-muted-foreground mb-2 text-balance leading-relaxed">
                {/* {mess || ( */}
                Please check your email and{" "}
                <span className="text-accent font-bold">
                  verify your account
                </span>{" "}
                to get started.
              </p>
            </div>

            {/* Action Button */}
            <div className="mb-6">
              <button
                onClick={() => (window.location.href = "/")}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 active:scale-95 shadow-lg hover:shadow-xl hover:scale-[1.02] transform"
              >
                Go Back to Home
              </button>
            </div>

            {/* Help Text */}
            <div className="pt-6 border-t border-border">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    Did not receive the email? Check your spam folder.
                  </span>
                </div>
                <a
                  href="mailto:support@agentdial.com"
                  className="text-sm text-accent hover:underline font-medium transition-colors"
                >
                  Contact our support team
                </a>
              </div>
            </div>
          </div>

          {/* Additional Decorative Elements */}
          <div className="absolute -z-10 top-10 left-10 w-20 h-20 bg-accent/5 rounded-full blur-2xl" />
          <div className="absolute -z-10 bottom-10 right-10 w-32 h-32 bg-accent/5 rounded-full blur-2xl" />
        </div>
      </main>
    );
  }

  return (
    <AuthLayout
      title="Get Started Today"
      subtitle="Create your AgentDial account and start building AI-powered voice agents in minutes."
      imageSide="left"
    >
      <Toaster />

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-md"></div>

          <div className="relative bg-background rounded-xl shadow-2xl p-8 w-full max-w-md border border-border z-10 animate-in fade-in zoom-in duration-300">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2 text-foreground">
                Who are you signing up as?
              </h2>
              <p className="text-muted-foreground">
                Choose your role so we can customize your workspace.
              </p>
            </div>

            <div className="space-y-3">
              {["agency", "merchant"].map((type) => (
                <button
                  key={type}
                  onClick={() => handleUserType(type)}
                  className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 group ${
                    selectedType === type
                      ? "bg-accent border-accent text-accent-foreground shadow-lg scale-[1.02]"
                      : "bg-background border-border hover:border-accent/50 hover:bg-accent/5 text-foreground"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        selectedType === type
                          ? "bg-accent-foreground/20"
                          : "bg-accent/10 group-hover:bg-accent/20"
                      }`}
                    >
                      {type === "agency" ? (
                        <svg
                          className={`w-5 h-5 ${
                            selectedType === type
                              ? "text-accent-foreground"
                              : "text-accent"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className={`w-5 h-5 ${
                            selectedType === type
                              ? "text-accent-foreground"
                              : "text-accent"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3
                        className={`font-bold text-lg mb-1 ${
                          selectedType === type
                            ? "text-accent-foreground"
                            : "text-foreground"
                        }`}
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </h3>
                      <p
                        className={`text-sm ${
                          selectedType === type
                            ? "text-accent-foreground/80"
                            : "text-muted-foreground"
                        }`}
                      >
                        {type === "agency"
                          ? "Manage multiple client accounts."
                          : "Create and grow your own workspace."}
                      </p>
                    </div>
                    {selectedType === type && (
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 rounded-full bg-accent-foreground flex items-center justify-center">
                          <Check className="w-4 h-4 text-accent" />
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="w-full">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Create Account
          </h2>

          {userType && (
            <div className="mb-6 p-5 bg-accent/15 border-2 border-accent/40 rounded-xl shadow-md backdrop-blur-sm">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex items-center justify-center w-14 h-14 bg-accent rounded-xl shadow-lg">
                    {userType === "agency" ? (
                      <svg
                        className="w-7 h-7 text-accent-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-7 h-7 text-accent-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1.5">
                      <span className="text-xl font-bold text-foreground">
                        {userType.charAt(0).toUpperCase() + userType.slice(1)}{" "}
                        Account
                      </span>
                      <span className="px-2.5 py-1 bg-accent text-accent-foreground text-xs font-bold rounded-md shadow-sm">
                        SELECTED
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground font-medium">
                      {userType === "agency"
                        ? "Manage multiple client accounts and scale your business"
                        : "Create and grow your own workspace"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(true);
                    setSelectedType(null);
                  }}
                  className="px-4 py-2.5 text-sm font-semibold text-accent hover:bg-accent/20 border-2 border-accent/40 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 whitespace-nowrap"
                >
                  Change
                </button>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm mb-2">First Name</label>
            <input
              {...form.register("firstName")}
              className="w-full px-4 py-3 bg-background border rounded-lg"
              placeholder="John"
            />
            <p className="text-red-500 text-sm">
              {form.formState.errors.firstName?.message}
            </p>
          </div>

          <div>
            <label className="block text-sm mb-2">Last Name</label>
            <input
              {...form.register("lastName")}
              className="w-full px-4 py-3 bg-background border rounded-lg"
              placeholder="Doe"
            />
            <p className="text-red-500 text-sm">
              {form.formState.errors.lastName?.message}
            </p>
          </div>

          <div>
            <label className="block text-sm mb-2">Work Email</label>
            <input
              {...form.register("workEmail")}
              type="email"
              className="w-full px-4 py-3 bg-background border rounded-lg"
              placeholder="you@company.com"
            />
            <p className="text-red-500 text-sm">
              {form.formState.errors.workEmail?.message}
            </p>
          </div>

          <div>
            <label className="block text-sm mb-2">Company Name</label>
            <input
              {...form.register("companyName")}
              className="w-full px-4 py-3 bg-background border rounded-lg"
              placeholder="Your Company"
            />
            <p className="text-red-500 text-sm">
              {form.formState.errors.companyName?.message}
            </p>
          </div>

          <button
            type="submit"
            className="w-full px-6 py-3 bg-accent text-white rounded-lg"
          >
            Create Account
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
